import {useMetrics, useReports} from '@sail/observability';
import {useStableCallback} from '@sail/react';
import {getEnvironment} from '@sail/utils';
import {useMemo} from 'react';
import {NavigationError} from 'src/errors';
import {useNavigationConfig} from 'src/internal/config/NavigationConfigContext';
import urlToRoutes from 'src/internal/urlToRoutes';
import useRouteToUrl from 'src/internal/useRouteToUrl';
import createRoute from 'src/public/createRoute/createRoute';
import useHref from 'src/public/useHref/useHref';
import useRoutes from 'src/public/useRoutes/useRoutes';

import type {NavigationOptions, Route, RouteNames} from 'src/types';

let visitedHref: string;

function throwHrefError(
  error: ReturnType<typeof useReports>['error'],
  href: string,
): never {
  const errorMessage = `No route matched for "${href}", is there a catch-all?`;

  if (getEnvironment() !== 'production') {
    error(errorMessage, {});
  }

  throw new NavigationError(errorMessage);
}

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-navigation/api/useRoute
 */
export default function useRoute(): [
  Required<Route>,
  (
    newRoute: Route | ((oldRoute: Route) => Route),
    options?: NavigationOptions,
  ) => void,
] {
  const {warning, error} = useReports();
  const {increment} = useMetrics();
  const config = useNavigationConfig();
  const [href, setHref] = useHref();
  const routeToUrl = useRouteToUrl();
  const routes = useRoutes();

  const routeName = useMemo(() => getFirstRouteName(routes), [routes]);

  if (!routeName) {
    throwHrefError(error, href);
  }

  const route = routes[routeName];
  const currentRoute = route ? createRoute(route) : route;

  if (!currentRoute) {
    throwHrefError(error, href);
  }

  if (visitedHref !== href) {
    // eslint-disable-next-line react-hooks/globals
    visitedHref = href;

    switch (routeName) {
      case 'CATCH_ALL':
        if (getEnvironment() !== 'production') {
          warning('The URL does not match any registered route');
        }

        increment('stripelets.navigation.cannot_match_route');

        break;

      case 'INVALID_URL':
        increment('stripelets.navigation.invalid_url');

        warning('The Invalid URL is detected');

        break;
    }
  }

  const setRoute = useStableCallback(
    (
      newRoute: Route | ((oldRoute: Route) => Route),
      options?: NavigationOptions,
    ) => {
      const normalizeOptions = options ?? {behavior: 'default'};

      if (typeof newRoute === 'function') {
        return setHref((prevHref) => {
          const routes = urlToRoutes(config, prevHref);
          const routeName = getFirstRouteName(routes);

          if (!routeName) {
            throwHrefError(error, prevHref);
          }

          const route = routes[routeName];
          const prevRoute = createRoute(route);

          return routeToUrl(newRoute(prevRoute), prevRoute);
        }, normalizeOptions);
      }

      return setHref(routeToUrl(newRoute, currentRoute), normalizeOptions);
    },
  );

  return [currentRoute, setRoute];
}

function getFirstRouteName(
  routes: ReturnType<typeof urlToRoutes>,
): RouteNames | undefined {
  for (const name in routes) {
    if (routes.hasOwnProperty(name)) {
      return name as RouteNames;
    }
  }
  return undefined;
}
